import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banknote, Wallet, PiggyBank, TrendingUp, Landmark, ArrowUpRight,
  Users, HandCoins, Receipt, Info, Loader2, RefreshCw, AlertTriangle,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { descargarPolizaPdf, POLIZA_SUBTIPO_LABEL } from "@/components/finanzas/PolizaCard";
import { toast } from "sonner";

function KpiCard({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700 ring-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    amber: "bg-amber-50 text-amber-800 ring-amber-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${toneMap[tone]} ring-inset`}
    >
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${toneMap[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      </div>
      <div className="mt-3 font-heading text-2xl font-semibold tracking-tight text-slate-900">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function SectionShortcut({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:shadow-md"
    >
      <div className="rounded-lg bg-slate-100 p-2 text-slate-700 group-hover:bg-slate-900 group-hover:text-white">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 font-medium text-slate-900">
          {title}
          <ArrowUpRight className="h-3.5 w-3.5 text-slate-400 transition group-hover:text-slate-900" />
        </div>
        <p className="mt-0.5 text-[13px] text-slate-500">{desc}</p>
      </div>
    </Link>
  );
}

export default function TesoreriaPanel() {
  const [fecha, setFecha] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [movs, setMovs] = useState([]);
  const [error, setError] = useState(null);

  const cargar = async (f) => {
    setLoading(true);
    setError(null);
    try {
      const params = f ? { fecha: f } : {};
      const [r, rm] = await Promise.all([
        api.get("/finanzas/tesoreria", { params }),
        api.get("/finanzas/movimientos", f ? { params: { fecha_hasta: f } } : {}),
      ]);
      setData(r.data);
      setMovs((rm.data?.movimientos || []).slice(0, 25));
    } catch (e) {
      setError(apiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fecha default = hoy
    const hoy = new Date().toISOString().slice(0, 10);
    setFecha(hoy);
    cargar(hoy);
  }, []);

  const tesoDisp = data?.tesoreria?.disponible ?? 0;
  const capacidadTono = tesoDisp > 0 ? "emerald" : tesoDisp < 0 ? "rose" : "slate";

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Panel de tesorería"
        subtitle="Vista consolidada de efectivo, bancos, capital, cartera y fondeo — leído directamente de la contabilidad."
      >
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-slate-500">Fecha de corte</Label>
            <Input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-40"
              data-testid="tesoreria-fecha-corte"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => cargar(fecha)}
            disabled={loading}
            data-testid="tesoreria-refrescar-btn"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Actualizar
          </Button>
        </div>
      </PageHeader>

      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <div
          data-testid="tesoreria-banner-derivada"
          className="mb-6 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-[13px] text-sky-900"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-semibold">Tesorería derivada de la contabilidad.</span>{" "}
            No hay un libro paralelo. Cada movimiento (aportación, fondeo, gasto) genera una póliza automatizada
            que actualiza estos saldos. La cartera vigente se toma directamente de los créditos.
          </div>
        </div>

        <h2 className="mb-3 font-heading text-lg font-semibold text-slate-900">Recursos líquidos</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            testid="kpi-efectivo"
            icon={Wallet}
            label="Efectivo (Caja 1101)"
            value={mxn(data?.tesoreria?.efectivo ?? 0)}
            hint="Cargos y abonos en caja"
            tone="amber"
          />
          <KpiCard
            testid="kpi-bancos"
            icon={Banknote}
            label="Bancos (1102)"
            value={mxn(data?.tesoreria?.bancos ?? 0)}
            hint="Saldo bancario consolidado"
            tone="sky"
          />
          <KpiCard
            testid="kpi-disponible"
            icon={PiggyBank}
            label="Tesorería disponible"
            value={mxn(tesoDisp)}
            hint="Efectivo + Bancos"
            tone={capacidadTono}
          />
          <KpiCard
            testid="kpi-capacidad"
            icon={TrendingUp}
            label="Capacidad para colocar"
            value={mxn(data?.indicadores_gestion?.capacidad_estimada_colocar ?? 0)}
            hint="Indicador de gestión (no regulatorio)"
            tone={capacidadTono}
          />
        </div>

        <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-slate-900">Cartera colocada</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard
            testid="kpi-cartera-vigente"
            icon={HandCoins}
            label="Cartera vigente"
            value={mxn(data?.cartera?.vigente ?? 0)}
            tone="emerald"
          />
          <KpiCard
            testid="kpi-cartera-vencida"
            icon={AlertTriangle}
            label="Cartera vencida"
            value={mxn(data?.cartera?.vencida ?? 0)}
            tone="rose"
          />
          <KpiCard
            testid="kpi-cartera-total"
            icon={Landmark}
            label="Cartera total"
            value={mxn(data?.cartera?.total ?? 0)}
            tone="indigo"
          />
        </div>

        <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-slate-900">Capital contable</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard
            testid="kpi-capital-pagado"
            icon={Users}
            label="Capital social pagado"
            value={mxn(data?.capital_contable?.capital_social_pagado ?? 0)}
            hint="Aportaciones exhibidas por los socios"
            tone="indigo"
          />
          <KpiCard
            testid="kpi-capital-noexhibido"
            icon={Users}
            label="Capital suscrito no exhibido"
            value={mxn(data?.capital_contable?.capital_no_exhibido ?? 0)}
            tone="slate"
          />
          <KpiCard
            testid="kpi-resultado-ejercicio"
            icon={TrendingUp}
            label="Resultado del ejercicio"
            value={mxn(data?.capital_contable?.resultado_ejercicio ?? 0)}
            hint="Utilidad / (pérdida) acumulada"
            tone={
              (data?.capital_contable?.resultado_ejercicio ?? 0) >= 0 ? "emerald" : "rose"
            }
          />
        </div>

        <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-slate-900">Pasivo bancario / Fondeo</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard
            testid="kpi-pasivo-cp"
            icon={Landmark}
            label="Corto plazo (2201)"
            value={mxn(data?.pasivo_bancario?.corto_plazo ?? 0)}
            hint="Préstamos por pagar ≤ 12 meses"
            tone="rose"
          />
          <KpiCard
            testid="kpi-pasivo-lp"
            icon={Landmark}
            label="Largo plazo (2202)"
            value={mxn(data?.pasivo_bancario?.largo_plazo ?? 0)}
            hint="Préstamos por pagar > 12 meses"
            tone="rose"
          />
          <KpiCard
            testid="kpi-pasivo-total"
            icon={Landmark}
            label="Pasivo bancario total"
            value={mxn(data?.pasivo_bancario?.total ?? 0)}
            tone="rose"
          />
        </div>

        {data?.indicadores_gestion?.leyenda && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4 text-[12px] text-slate-500">
            <span className="font-medium text-slate-700">Nota:</span>{" "}
            {data.indicadores_gestion.leyenda}
          </div>
        )}

        <h2 className="mb-3 mt-10 font-heading text-lg font-semibold text-slate-900">Registrar movimientos</h2>
        <p className="mb-4 text-sm text-slate-500">
          Cada operación registrada aquí genera su póliza contable, aparece en el visor de
          pólizas del periodo y se integra a la Balanza, al Estado de Resultados y al Balance
          General del módulo Contabilidad y Fiscal (estados preliminares, con acumulación
          mensual).
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SectionShortcut
            testid="shortcut-socios"
            to="/finanzas/socios"
            icon={Users}
            title="Socios y aportaciones"
            desc="Alta de socios y captura de aportaciones de capital (Dr Caja/Bancos · Cr Capital social)."
          />
          <SectionShortcut
            testid="shortcut-fondeo"
            to="/finanzas/fondeo"
            icon={HandCoins}
            title="Préstamos recibidos"
            desc="Fondeo bancario y comercial. Pagos separados en capital + interés (5501)."
          />
          <SectionShortcut
            testid="shortcut-gastos"
            to="/finanzas/gastos"
            icon={Receipt}
            title="Gastos operativos"
            desc="Nómina, renta, servicios, comisiones bancarias y demás egresos de operación."
          />
        </div>

        <h2 className="mb-1 mt-10 font-heading text-lg font-semibold text-slate-900">
          Últimos movimientos con póliza
        </h2>
        <p className="mb-3 text-sm text-slate-500">
          Pólizas registradas desde Administración financiera. Las mismas que ahora alimentan la
          Balanza, el Estado de Resultados y el Balance General.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="tabla-movimientos-finanzas">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Fecha</th>
                <th className="px-4 py-2 text-left">Concepto</th>
                <th className="px-4 py-2 text-left">Referencia</th>
                <th className="px-4 py-2 text-left">Cuentas</th>
                <th className="px-4 py-2 text-right">Importe</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {movs.length === 0 && (
                <tr><td colSpan={6} data-testid="movimientos-finanzas-empty"
                        className="px-4 py-8 text-center text-slate-400">
                  Sin movimientos registrados en Administración financiera.
                </td></tr>
              )}
              {movs.map((m) => (
                <tr key={m.id} className="border-t border-slate-100"
                    data-testid={`mov-finanzas-${(m.id || "").slice(0, 6)}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{m.fecha}</td>
                  <td className="px-4 py-2">{POLIZA_SUBTIPO_LABEL[m.subtipo] || m.subtipo}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">{m.referencia}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">
                    {(m.movimientos || []).map((x) => x.codigo).join(" · ")}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{mxn(m.total_cargos)}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 px-2"
                            data-testid={`mov-finanzas-pdf-${(m.id || "").slice(0, 6)}`}
                            onClick={async () => {
                              try { await descargarPolizaPdf(m); }
                              catch (e) { toast.error(apiError(e)); }
                            }}>
                      PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
